import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import PageHead from '@/components/PageHead/PageHead';
import { MagazineContextProvider } from '@/contexts/Magazine/Magazine.context';
import { MagazineContextProviderProps } from '@/contexts/Magazine/interfaces';
import { extractContents } from '@/helpers/extractContents';
import { extractItemListFromContent } from '@/helpers/extractItemListFromContent';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import type { PageModel } from '@/models/page';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import MagazineArticleFooter from '@uikit/components/Magazine/MagazineArticleFooter/MagazineArticleFooter';
import { ArticleHeaderAnchorItem } from '@uikit/components/Magazine/MagazineArticleHeader/interfaces';
import MagazineArticleLayout from '@uikit/components/Magazine/MagazineArticleLayout/MagazineArticleLayout';
import MagazineMain from '@uikit/components/Magazine/MagazineMain/MagazineMain';
import MagazinePageHeader from '@uikit/components/Magazine/MagazinePageHeader/MagazinePageHeader';
import MagazineStageBox from '@uikit/components/Magazine/MagazineStageBox/MagazineStageBox';
import MagazineStageBoxArticle from '@uikit/components/Magazine/MagazineStageBoxArticle/MagazineStageBoxArticle';
import NewHeader from '@uikit/components/NewHeader/NewHeader';
import PromoBanner from '@uikit/components/PromoBanner/PromoBanner';
import MagazineArticleHeroArea from '@uikit/composites/HeroArea/MagazineArticleHeroArea';
import React from 'react';
import getBaseUrl from '../../uikit/helpers/getBaseUrl';
import { CmsComponentsContext } from '../CmsComponentsContext';
import cmsComponents from './MagazineArticlePage.cmsComponents';
import type { MagazineArticlePageProps } from './interfaces';

const baseUrl = getBaseUrl();

const MagazineArticlePage = (
  routeData: PageModel<MagazineArticlePageProps>
) => {
  const { contents, modals, path, props } = routeData;
  const {
    image,
    subTitle,
    title,
    publicationDate,
    category,
    contentFormat,
    customerGroup,
    tags,
    ogImage,
    categoryTitle,
    categoryPath,
    additionalInfo,
  } = props;

  const meta = extractPageMetaFromRouteData(routeData, {
    ogImage: ogImage?.path ? ogImage : image,
    ogType: 'article',
  });

  const {
    extractedContents: [magazineArticleDetailsArea, magazineContents],
    restContents,
  } = extractContents(
    ['magazine-article-details-area', 'magazine-section'],
    contents
  );

  const magazineArticleContextValue = React.useMemo(() => {
    const value: MagazineContextProviderProps['value'] = {
      currentCategory: category,
    };

    if (publicationDate) {
      value.publicationDate = new Date(publicationDate);
    }

    return value;
  }, [category, publicationDate]);

  const { items, restContents: _restContents } =
    extractItemListFromContent<ArticleHeaderAnchorItem>(
      'anchorItems',
      restContents
    );

  useInitialPageDataLayer([
    {
      event: 'pageView',
      ...(tags ? { magContentTopic: tags.join(', ') } : {}),
      ...(category ? { magRessort: category } : {}),
      ...(contentFormat ? { magContentFormat: contentFormat } : {}),
      ...(customerGroup ? { magTargetCustomerType: customerGroup } : {}),
    },
  ]);

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <PageHead {...meta} />

      <MagazineContextProvider value={magazineArticleContextValue}>
        <MagazineArticleLayout>
          <NewHeader
            hasPageHeader
            isFullWidth
            isInverted
            isSticky
            renderPageHeader={(props) => (
              <MagazinePageHeader
                {...props}
                anchors={items}
                category={category}
                categorySlug={categoryPath}
                categoryTitle={categoryTitle}
                title={title}
              />
            )}
            variant="enercity"
          />

          <MagazineMain key={props.id}>
            <MagazineArticleHeroArea image={image} />

            <MagazineContextProvider value={magazineArticleContextValue}>
              <MagazineStageBox>
                <MagazineStageBoxArticle
                  title={title}
                  subTitle={subTitle}
                  articlePagePath={baseUrl + path}
                />
              </MagazineStageBox>
            </MagazineContextProvider>

            <ContentsToReact contents={magazineArticleDetailsArea} />

            <ContentsToReact contents={magazineContents} />

            <MagazineContextProvider value={magazineArticleContextValue}>
              <MagazineArticleFooter
                date={publicationDate}
                tags={tags}
                title={title}
                articlePagePath={baseUrl + path}
                category={category}
                additionalInfo={additionalInfo}
              />
            </MagazineContextProvider>

            <ContentsToReact contents={_restContents} />
          </MagazineMain>
          <EnercityFooter />
        </MagazineArticleLayout>
      </MagazineContextProvider>

      <GenericModals modals={modals} />
      {routeData.props.promoBannerVisible === 'promoBanner' &&
        routeData.props.promoBanner && (
          <PromoBanner banner={routeData.props.promoBanner} />
        )}
    </CmsComponentsContext.Provider>
  );
};

export default MagazineArticlePage;
